import { useEffect, useState } from "react";

interface UnlimitedActiveModalProps {
  endsAt: string | null | undefined;
  onClose: () => void;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "0:00";
  const totalSec = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function UnlimitedActiveModal({
  endsAt,
  onClose,
}: UnlimitedActiveModalProps) {
  const endsAtMs = endsAt ? new Date(endsAt).getTime() : 0;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAtMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAtMs]);

  const remainingMs = endsAtMs ? Math.max(0, endsAtMs - now) : 0;

  return (
    <div
      className="!border-0 !outline-0 !ring-0"
      style={{ border: "none", outline: "none" }}
    >
      <div className="bg-componentBackground dark:bg-black border-2 border-emerald-500 rounded-3xl p-6 max-w-sm w-[90vw] md:w-full text-center">
        <div className="text-4xl mb-2">🔥</div>
        <h2 className="text-xl font-bold text-emerald-400 mb-4">
          Unlimited Active
        </h2>

        <div className="border-t border-b border-emerald-500/30 py-4 mb-4">
          <div className="text-text2 dark:text-gray-400 text-xs uppercase tracking-wide mb-1">
            Time Remaining
          </div>
          <div className="text-5xl font-bold text-white tabular-nums">
            {formatRemaining(remainingMs)}
          </div>
        </div>

        <p className="text-text2 dark:text-gray-300 text-sm mb-3">
          Send unlimited messages — no credits charged until your window ends.
        </p>

        <div className="border border-emerald-500/40 bg-emerald-500/10 rounded-lg px-3 py-2 mb-5 text-left">
          <div className="flex items-start gap-2">
            <div className="text-lg leading-none mt-0.5">✦</div>
            <div className="flex-1">
              <div className="text-emerald-300 text-xs font-semibold uppercase tracking-wide">
                Your profile is boosted
              </div>
              <div className="text-text2 dark:text-gray-400 text-xs mt-0.5">
                Stay active and keep messaging — boosted profiles get more attention.
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2.5 px-4 rounded-lg font-semibold text-sm active:scale-95 transition-transform"
        >
          Back to Chatting
        </button>
      </div>
    </div>
  );
}
